<template>
  <NotFound
    v-if="isGambitNotFound && !isPendingRemoval && !leftRemovedGambit && !mergedIntoGambitId"
    :title="localize('gambit.not_found.title')"
  >
    <p>{{ localize('gambit.not_found.message') }}</p>

    <GamButton @click="navigateToSparks" :variant="GamButtonVariant.SECONDARY" :size="GamButtonSize.SMALL" is-centered>
      {{ localize('gambit.not_found.explore') }}
    </GamButton>
  </NotFound>

  <GamMainView v-else class="gambits-view">
    <GambitOverlay
      v-if="gambitDetails"
      :gambit="gambitDetails"
      :is-open="true"
      :key="String(route.params.id)"
      @close="handleClose"
    />
  </GamMainView>
</template>

<script lang="ts" setup>
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import { localize } from '@/core/gambits';
import { useAppStore } from '@/stores/app.store';
import { useGambitStore } from '@/stores/gambit.store';
import GamButton from '@/views/components/GamButton.vue';
import GamMainView from '@/views/components/GamMainView.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import NotFound from '@/views/NotFound.vue';
import { storeToRefs } from 'pinia';
import { nextTick, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import GambitOverlay from './GambitOverlay.vue';

const router = useRouter();
const route = useRoute();
const appStore = useAppStore();

if (appStore.isFirstVisit() && window.location.search.includes('back')) {
  router.replace({ ...route, query: { ...route.query, back: undefined } });
}

const gambitStore = useGambitStore();
const {
  gambit: gambitDetails,
  isNotFound: isGambitNotFound,
  mergedIntoGambitId,
  leftRemovedGambit,
  isPendingRemoval,
} = storeToRefs(gambitStore);
const isClosing = ref(false);

watch(mergedIntoGambitId, (survivingId) => {
  if (!survivingId) return;
  mergedIntoGambitId.value = null;
  router.replace({ name: RouterNameType.GAMBIT, params: { id: survivingId } });
});

const returnFromMissingGambit = () => {
  if (route.query.back && window.history.length > 1) {
    router.back();
  } else {
    router.replace({ name: RouterNameType.SPARKS });
  }
};

watch(leftRemovedGambit, (hasLeft) => {
  if (!hasLeft) return;
  leftRemovedGambit.value = false;
  returnFromMissingGambit();
});

watch(
  (): string[] => [String(route.params.id), String(route.query.reload)],
  async (next, prev) => {
    const [id, reload] = next;
    const [prevId] = prev || [];

    if (!id || (!reload && id === prevId)) {
      return;
    }

    await gambitStore.fetch(String(id));

    if (reload === 'true') {
      nextTick(() => {
        router.replace({ ...route, query: { ...route.query, reload: undefined } });
      });
    }
  },
  { immediate: true },
);

const handleClose = () => {
  if (isClosing.value) return;
  isClosing.value = true;

  if (route.query.back === 'history') {
    router.back();
  } else {
    router.push({ name: RouterNameType.SPARKS });
  }
};

const navigateToSparks = () => {
  router.push({ name: RouterNameType.SPARKS });
};
</script>

<style scoped lang="scss"></style>
